import { organizationMarkTone, type OrganizationMarkTone } from '@icebreaker/shared';
import { Avatar, type AvatarSize, type AvatarTone } from './avatar';

export interface OrgMarkProps {
  /** Kurum adı: logo yoksa köşeli monogram bu addan türer. */
  readonly name: string;
  /**
   * Kurumun MARKA ALANINDAKİ logo adresi (`organizations.logo_media_id` →
   * `logoUrl`). ZORUNLUDUR ve isteğe bağlı değildir: yeni bir kurum yüzeyi
   * logoyu okuyup okumayacağına derlemede karar verir. Kişi avatarı buraya
   * verilmez (B-18: kurum hesabının kişi fotoğrafı kurumun logosu değildir).
   */
  readonly logoUrl: string | null;
  /**
   * Kurumun AA tonu (beyaz yazıyla okunan, zincirden çözülmüş); kurumun kendi
   * rengi yoksa ya da yüzeyin verisi rengi taşımıyorsa `null` ve işaret koyu
   * mor zemine (`ink` hâli) düşer. ZORUNLUDUR (logo gibi): değer çağıranda shared kuralından
   * çözülmüş gelir (kayıt yolu `orgMarkFill`, çözülmüş marka yolu ya da
   * sunucunun `brandFill`i); bileşen zinciri bilmez. Kapı: `packages/shared/src/design/org-mark-tone.guard.spec.ts`.
   */
  readonly fill: string | null;
  readonly size?: AvatarSize;
  /**
   * ERİŞİLEBİLİR AD. Verilmezse işaret DEKORATİFTİR (logo `alt=""`, monogram
   * `aria-hidden`): kurum işaretlerinin neredeyse tamamı kurum adının hemen
   * yanında durur ve ad iki kez okunmasın. Verilirse logo bu `alt`la, monogram
   * `role="img"` + aynı `aria-label`la okunur; baş harfler harf harf okunmaz.
   * Yalnız logo varken ve tasarımın etiketlediği yerde verilir (künye
   * `organizerPage.logoAlt`, marka ekranı önizlemesi); logosuz monogram "logo"
   * diye okunmasın.
   */
  readonly alt?: string;
  readonly className?: string;
}

/**
 * Üç hâlin zemini (kullanıcı kararı 14.10.2026, kurum her yerde aynı görünür):
 * logo karosu BEYAZ ve temayla DEĞİŞMEZ (saydam PNG logo koyu temada ve renkli
 * bantta okunsun); marka zemini beyaz baş harfle ve temayla DEĞİŞMEZ; logosuz ve
 * renksiz kurum (`ink` hâli) KOYU MOR zemin, beyaz harf, iki temada ve kapı
 * bandında aynı (kullanıcı kararı 30.10.2026, 14.10'un "ink zemin" kararını web
 * için geçersiz kılar; tasarım README kurum monogramı ve PanelKapi
 * `.wb-gate__top .wb-orglogo`). Değerler web token borusundandır (`fixed-*` iki
 * temada sabit adlar); mobil tokenı okunmaz.
 */
function avatarTone(tone: OrganizationMarkTone): AvatarTone {
  switch (tone.kind) {
    case 'brand':
      return { id: 'brand', background: tone.fill, foreground: 'var(--ib-color-fixed-white)' };
    case 'ink':
      return {
        id: 'ink',
        background: 'var(--ib-color-fixed-primary)',
        foreground: 'var(--ib-color-fixed-white)',
      };
    case 'logo':
      return {
        id: 'logo',
        background: 'var(--ib-color-fixed-white)',
        foreground: 'var(--ib-color-fixed-ink)',
      };
  }
}

/**
 * KURUM İŞARETİ (B-18). Kurumun kimliğini çizen HER yüzey bu bileşenden geçer:
 * logo varsa logo, yoksa köşeli monogram. Şekil sabittir (`entity`), çağıran
 * seçemez. Zemin shared kuralından okunur (`organizationMarkTone`): addan
 * türeyen sıcak ton kurum işaretinde KULLANILMAZ. Kapılar:
 * `packages/shared/src/design/org-mark.guard.spec.ts` (logo) ve
 * `packages/shared/src/design/org-mark-tone.guard.spec.ts` (zemin).
 */
export function OrgMark({ name, logoUrl, fill, size = 'md', alt, className }: OrgMarkProps) {
  return (
    <Avatar
      name={name}
      photoUrl={logoUrl}
      size={size}
      shape="entity"
      alt={alt ?? ''}
      label={alt}
      className={className}
      tone={avatarTone(organizationMarkTone({ logoUrl, fill }))}
    />
  );
}
